import { useEffect, useRef } from "react";
import { useLocation, useNavigationType, useSearchParams } from "react-router-dom";
import { useBaseline } from "@/lib/hooks/resume";

/**
 * Whether the browser's address has moved past `key`. A move shows only
 * once the next page is loaded, and until then this one is still up. The
 * browser's history is ahead of it, and in a memory router there is none.
 */
function addressMovedOn(key: string): boolean {
    const current = (window.history.state as { key?: string } | null)?.key;
    return current !== undefined && current !== key;
}

/**
 * Jobs, arrived at with no words, searches the latest job title on the
 * person's resume as it stands, so a new resume shows on the next visit.
 * Once each arrival: words they clear stay cleared until they next come
 * to the page. An arrival with words, or with a posting open, keeps its
 * own, and one that is already leaving writes nothing: a write there would
 * pull the person back from the page the sign-in sent them to. Returns the
 * title.
 */
export function useTitleSearch(
    standing: "member" | "visitor" | "unknown" = "member",
): string | null {
    const enabled = standing === "member";
    const [params, setParams] = useSearchParams();
    const { key } = useLocation();
    const navigation = useNavigationType();
    const baseline = useBaseline(enabled);
    const title = baseline.data?.latest_title ?? null;
    // The arrival still waiting on its words. A replace is the page
    // writing its own address (the search box, a filter), not an arrival.
    const arrival = useRef<string | null>(key);

    useEffect(() => {
        if (navigation !== "REPLACE") arrival.current = key;
    }, [key, navigation]);

    useEffect(() => {
        if (arrival.current !== key) return;
        if (params.has("q") || params.has("posting")) {
            arrival.current = null;
            return;
        }
        // Still reading the sign-in: the arrival waits for it.
        if (standing === "unknown") return;
        if (!enabled) {
            arrival.current = null;
            return;
        }
        if (baseline.isPending) return;
        arrival.current = null;
        if (!title || addressMovedOn(key)) return;
        setParams(
            (current) => {
                const next = new URLSearchParams(current);
                next.set("q", title);
                return next;
            },
            { replace: true },
        );
    }, [key, params, baseline.isPending, title, setParams, enabled, standing]);

    return title;
}
